'use client';

import { useState, useEffect } from 'react';
import { useUser } from '@/lib/context/user-context';
import { useUserSettings } from '@/contexts/user-settings-context';
import { toast } from 'sonner';

export interface Playlist {
  id: string;
  name: string;
  description?: string;
  episodes: string[];
  createdAt: string;
  updatedAt: string;
}

export const usePlaylists = (autoLoad: boolean = false) => {
  const { user } = useUser();
  const { userSettings, setUserSettings } = useUserSettings();
  const [playlistEpisodes, setPlaylistEpisodes] = useState<{[playlistId: string]: any[]}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedEpisodes, setHasLoadedEpisodes] = useState(false);

  const generatePlaylistId = () => {
    return `playlist_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  };

  const createPlaylist = async (name: string, description?: string, initialEpisodeId?: string) => {
    console.log('🎵 Creating playlist:', { name, description, initialEpisodeId });

    if (!user?.id) {
      toast.error('Please login to create playlists');
      return null;
    }

    if (!name.trim()) {
      toast.error('Playlist name is required');
      return null;
    }

    try {
      const playlistId = generatePlaylistId();
      const newPlaylist: Playlist = {
        id: playlistId,
        name: name.trim(),
        description: description?.trim(),
        episodes: initialEpisodeId ? [initialEpisodeId] : [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const currentPlaylists = userSettings.playlists || {};
      const updatedPlaylists = {
        ...currentPlaylists,
        [playlistId]: newPlaylist
      };

      // Update local state (optimistic update)
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: updatedPlaylists 
      }));

      // Update backend
      const response = await fetch('/api/user-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          settingKey: 'playlists',
          value: updatedPlaylists
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create playlist');
      }

      const successMessage = initialEpisodeId 
        ? `Playlist "${name}" created and episode added`
        : `Playlist "${name}" created successfully`;
      toast.success(successMessage);
      console.log('🎵 Playlist created:', newPlaylist);
      return newPlaylist;

    } catch (error) {
      console.error('Error creating playlist:', error);
      
      // Rollback local state
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: userSettings.playlists || {} 
      }));
      
      toast.error('Failed to create playlist. Please try again.');
      return null;
    }
  };

  const addEpisodeToPlaylist = async (playlistId: string, episodeId: string) => {
    console.log('🎵 Adding episode to playlist:', { playlistId, episodeId });

    if (!user?.id) {
      toast.error('Please login to manage playlists');
      return false;
    }

    const currentPlaylists = userSettings.playlists || {};
    const playlist = currentPlaylists[playlistId];

    if (!playlist) {
      toast.error('Playlist not found');
      return false;
    }

    if (playlist.episodes.includes(episodeId)) {
      toast.info('Episode is already in this playlist');
      return false;
    }

    try {
      const updatedPlaylist = {
        ...playlist,
        episodes: [...playlist.episodes, episodeId],
        updatedAt: new Date().toISOString(),
      };

      const updatedPlaylists = {
        ...currentPlaylists,
        [playlistId]: updatedPlaylist
      };

      // Update local state (optimistic update)
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: updatedPlaylists 
      }));

      // Update backend
      const response = await fetch('/api/user-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          settingKey: 'playlists',
          value: updatedPlaylists
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add episode to playlist');
      }

      toast.success(`Added to "${playlist.name}"`);
      return true;

    } catch (error) {
      console.error('Error adding episode to playlist:', error);
      
      // Rollback local state
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: userSettings.playlists || {} 
      }));
      
      toast.error('Failed to add episode. Please try again.');
      return false;
    }
  };

  const removeEpisodeFromPlaylist = async (playlistId: string, episodeId: string) => {
    console.log('🎵 Removing episode from playlist:', { playlistId, episodeId });

    if (!user?.id) {
      toast.error('Please login to manage playlists');
      return false;
    }

    const currentPlaylists = userSettings.playlists || {};
    const playlist = currentPlaylists[playlistId];

    if (!playlist) {
      toast.error('Playlist not found');
      return false;
    }

    try {
      const updatedPlaylist = {
        ...playlist,
        episodes: playlist.episodes.filter(id => id !== episodeId),
        updatedAt: new Date().toISOString(),
      };

      const updatedPlaylists = {
        ...currentPlaylists,
        [playlistId]: updatedPlaylist
      };

      // Update local state (optimistic update)
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: updatedPlaylists 
      }));

      // Update backend
      const response = await fetch('/api/user-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          settingKey: 'playlists',
          value: updatedPlaylists
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove episode from playlist');
      }

      toast.success(`Removed from "${playlist.name}"`);
      return true;

    } catch (error) {
      console.error('Error removing episode from playlist:', error);
      
      // Rollback local state
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: userSettings.playlists || {} 
      }));
      
      toast.error('Failed to remove episode. Please try again.');
      return false;
    }
  };

  const deletePlaylist = async (playlistId: string) => {
    console.log('🎵 Deleting playlist:', playlistId);

    if (!user?.id) {
      toast.error('Please login to manage playlists');
      return false;
    }

    const currentPlaylists = userSettings.playlists || {};
    const playlist = currentPlaylists[playlistId];

    if (!playlist) {
      toast.error('Playlist not found');
      return false;
    }

    try {
      const updatedPlaylists = { ...currentPlaylists };
      delete updatedPlaylists[playlistId];

      // Update local state (optimistic update)
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: updatedPlaylists 
      }));

      // Update backend
      const response = await fetch('/api/user-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          settingKey: 'playlists',
          value: updatedPlaylists
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete playlist');
      }

      toast.success(`Playlist "${playlist.name}" deleted`);
      return true;

    } catch (error) {
      console.error('Error deleting playlist:', error);
      
      // Rollback local state
      setUserSettings(prev => ({ 
        ...prev, 
        playlists: userSettings.playlists || {} 
      }));
      
      toast.error('Failed to delete playlist. Please try again.');
      return false;
    }
  };

  const isEpisodeInPlaylist = (playlistId: string, episodeId: string): boolean => {
    if (!episodeId || !playlistId) return false;
    const playlist = userSettings.playlists?.[playlistId];
    return playlist?.episodes.includes(episodeId) || false;
  };

  const getPlaylistsContainingEpisode = (episodeId: string): Playlist[] => {
    if (!episodeId) return [];
    const playlists = userSettings.playlists || {};
    return Object.values(playlists).filter(playlist => 
      playlist.episodes.includes(episodeId)
    );
  };

  // Fetch episode data for all playlists
  const fetchPlaylistEpisodes = async () => {
    const playlists = userSettings.playlists || {};
    if (Object.keys(playlists).length === 0) {
      setPlaylistEpisodes({});
      setHasLoadedEpisodes(true);
      return;
    }

    setIsLoading(true);
    try {
      const playlistEpisodesData: {[playlistId: string]: any[]} = {};
      
      await Promise.all(
        Object.entries(playlists).map(async ([playlistId, playlist]) => {
          if (playlist.episodes.length === 0) {
            playlistEpisodesData[playlistId] = [];
            return;
          }

          const episodesData = await Promise.all(
            playlist.episodes.map(async (episodeId) => {
              try {
                const response = await fetch(`/api/episodes/${episodeId}`);
                if (response.ok) {
                  const data = await response.json();
                  return {
                    shareId: episodeId,
                    title: data.content?.title,
                    author: data.content?.author,
                    ownerAvatarUrl: data.content?.ownerAvatarUrl,
                    thumbnail: data.content?.thumbnail,
                    publishDate: data.content?.publishDate,
                    duration: data.content?.duration,
                    videoId: data.content?.videoId,
                  };
                }
                return { shareId: episodeId };
              } catch {
                return { shareId: episodeId };
              }
            })
          );
          
          playlistEpisodesData[playlistId] = episodesData;
        })
      );

      setPlaylistEpisodes(playlistEpisodesData);
      setHasLoadedEpisodes(true);
    } catch (error) {
      console.error('Error fetching playlist episodes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Only auto-fetch episode data if autoLoad is enabled
  useEffect(() => {
    if (autoLoad) {
      fetchPlaylistEpisodes();
    }
  }, [userSettings.playlists, autoLoad]);

  return {
    playlists: Object.values(userSettings.playlists || {}),
    playlistsMap: userSettings.playlists || {},
    episodes: playlistEpisodes,
    isLoading,
    createPlaylist,
    addEpisodeToPlaylist,
    removeEpisodeFromPlaylist: async (playlistId: string, episodeId: string) => {
      const success = await removeEpisodeFromPlaylist(playlistId, episodeId);
      if (success) {
        // Refresh playlist episodes after removal
        fetchPlaylistEpisodes();
      }
      return success;
    },
    deletePlaylist,
    isEpisodeInPlaylist,
    getPlaylistsContainingEpisode,
    playlistsCount: Object.keys(userSettings.playlists || {}).length,
    // Manual loading functions
    loadPlaylistEpisodes: fetchPlaylistEpisodes,
    hasLoadedEpisodes,
  };
};